import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { HeatmapLayerProps } from '@react-google-maps/api';
import { GoogleMap, Marker, MarkerClusterer, useJsApiLoader } from '@react-google-maps/api';
import ChartCard from 'c-components/HiddenReport/Components/ChartCard';
import { DeckHeatmapLayer } from 'c-components/DeckHeatmapLayer';
import poweredByUniledLogo from 'public/poweredByUniledLogo.png';

import { generateHeatMapData, generateMarkerLocations, ReportingMapOptions } from 'c-main/Lib';
import type { PCAMapReportResponseMinimal } from 'c-sdk';
import { MapDataType } from 'c-main/Components/Campaign/CampaignReports/types';

type MapReportData = PCAMapReportResponseMinimal['reportData'];

type FooterProps = { title: string; campaignId: number | string; brand: string };

type MapPaneProps = {
    title: string;
    data: MapReportData;
    mode: 'heat' | 'markers';
    id?: string;
};

type OneMapPageProps = {
    mainTitle: string;
    brand: string;
    logoUrl: string;
    footer: FooterProps;
    map: MapPaneProps;
};

const SCREEN_MAP_HEIGHT = 780;
const PRINT_MAP_HEIGHT = 560;

const DEFAULT_CENTER = { lat: 54.5, lng: -3.1 };
const DEFAULT_ZOOM = 5;
const MAX_ZOOM_AFTER_FIT = 10;
const ZOOM_OUT_AFTER_FIT = 0.5;

const mapContainerStyle = { width: '100%', height: '100%' };

const PRINT_SAFE_CSS = `
/* Keep header/footer above any map layers */
.page .page-header, .page .page-footer { z-index: 1000; position: absolute; }

/* Reserve space so content doesn't render under the absolute footer */
.page--maps {
  position: relative;
  padding-bottom: var(--footer-h);
}

/* Pin footer to bottom */
.page--maps .page-footer {
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1200;
}

/* MapPane layering */
.mappane { position: relative; contain: layout paint size; z-index: 1; }
.mappane .layer { position: absolute; inset: 0; }
.mappane .layer img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Default on screen: show live, hide snapshot */
.mappane .layer.live     { visibility: visible; }
.mappane .layer.snapshot { visibility: hidden; }

/* --- maps page–only tweaks --- */
.page--maps { --footer-h: 84px; }   /* was 64px */
.page--maps .page-footer { z-index: 1200; }

/* PRINT: Only swap to snapshot if it's READY (prevents blank prints) */
@media print {
  .page { overflow: hidden !important; }
  .page--maps .mappane { height: ${PRINT_MAP_HEIGHT}px !important; }

  /* default: keep live visible as fallback */
  .mappane .layer.live { visibility: visible !important; }
  .mappane .layer.snapshot { visibility: hidden !important; }

  /* if snapshot loaded, print it instead (avoids Google popup) */
  .mappane.snapshot-ready .layer.live { visibility: hidden !important; }
  .mappane.snapshot-ready .layer.snapshot { visibility: visible !important; }

  /* avoid cropping snapshot in PDF */
  .mappane .layer.snapshot img {
    object-fit: contain !important;
    background: #fff;
  }
}
`;

const ReportHeader: React.FC<{ brand: string; logoUrl: string }> = ({ brand, logoUrl }) => (
    <Box sx={{ pl: 3, pt: 2, display: 'flex', alignItems: 'flex-end', gap: 2, width: 1 }}>
        <Box
            component="img"
            src={logoUrl}
            alt="Logo"
            className="header-logo"
            sx={{ maxHeight: 48, maxWidth: 270, objectFit: 'contain', display: 'block' }}
        />
    </Box>
);

const ReportFooter: React.FC<FooterProps> = ({ title, campaignId, brand }) => (
    <Box>
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, py: 0.5 }}
        >
            <Typography sx={{ fontWeight: 800 }} pl={5}>
                {title} | # {campaignId}
            </Typography>
            <Box
                pr={2}
                component="img"
                src={poweredByUniledLogo}
                alt="Powered by UniLED"
                sx={{ maxHeight: 48, maxWidth: 270, objectFit: 'contain', display: 'block' }}
            />
        </Box>
        <Box sx={{ height: 40, bgcolor: brand }} mt={1.5} />
    </Box>
);

function useBoundsFromData(data: MapReportData, isLoaded?: boolean) {
    return useMemo(() => {
        const keys = Object.keys(data ?? {});
        if (!keys.length || !isLoaded || typeof window === 'undefined' || !(window as any).google)
            return null;

        const g = (window as any).google;
        const bounds = new g.maps.LatLngBounds();
        const impactPoints: Array<{ lat: number; lng: number }> = [];
        const fallbackPoints: Array<{ lat: number; lng: number }> = [];

        keys.forEach(k => {
            const row = (data as any)[k];
            const actual = Number(row?.[0]);
            const lat = Number(row?.[6]);
            const lng = Number(row?.[7]);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                fallbackPoints.push({ lat, lng });
                if (actual > 0) {
                    impactPoints.push({ lat, lng });
                }
            }
        });

        const points = impactPoints.length > 0 ? impactPoints : fallbackPoints;
        if (!points.length) return null;

        points.forEach(({ lat, lng }) => {
            bounds.extend(new g.maps.LatLng(lat, lng));
        });

        try {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const latDiff = Math.abs(ne.lat() - sw.lat());
            const lngDiff = Math.abs(ne.lng() - sw.lng());
            const minSize = 1;
            if (latDiff < minSize || lngDiff < minSize) {
                const c = bounds.getCenter();
                bounds.extend(new g.maps.LatLng(c.lat() + minSize / 2, c.lng() + minSize / 2));
                bounds.extend(new g.maps.LatLng(c.lat() - minSize / 2, c.lng() - minSize / 2));
            }
        } catch {
            return null;
        }

        return bounds;
    }, [data, isLoaded]);
}

function padBounds(b: google.maps.LatLngBounds, padRatio = 0.14) {
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    const latPad = (ne.lat() - sw.lat()) * padRatio;
    const lngPad = (ne.lng() - sw.lng()) * padRatio;

    const g = (window as any).google;
    const padded = new g.maps.LatLngBounds();
    padded.extend(new g.maps.LatLng(sw.lat() - latPad, sw.lng() - lngPad));
    padded.extend(new g.maps.LatLng(ne.lat() + latPad, ne.lng() + lngPad));
    return padded;
}

const toHex = (c: string) => {
    if (/^#[0-9a-f]{6}$/i.test(c)) return c.slice(1);
    if (/^#[0-9a-f]{3}$/i.test(c))
        return c
            .slice(1)
            .split('')
            .map(x => x + x)
            .join('');
    const m = c.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!m) return '1f2937';
    const h = (n: string) => (+n).toString(16).padStart(2, '0');
    return `${h(m[1])}${h(m[2])}${h(m[3])}`;
};

function buildStaticMapUrl({
    apiKey,
    markers,
    bounds,
    centerZoom,
    brand = '#1f2937',
    size = { w: 640, h: 640 },
    scale = 2,
}: {
    apiKey: string;
    markers?: { lat: number; lng: number }[];
    bounds?: google.maps.LatLngBounds | null;
    centerZoom?: { lat: number; lng: number; zoom: number } | null;
    brand?: string;
    size?: { w: number; h: number };
    scale?: number;
}) {
    const base = 'https://maps.googleapis.com/maps/api/staticmap';
    const sizeStr = `size=${size.w}x${size.h}&scale=${scale}&maptype=roadmap`;
    const color = toHex(brand);

    const ms = markers ?? [];
    const step = Math.ceil(ms.length / 80) || 1; // ≤80 pts total
    const slim = ms.filter((_, i) => i % step === 0);
    const chunks: string[] = [];
    for (let i = 0; i < slim.length; i += 20) {
        const group = slim
            .slice(i, i + 20)
            .map(m => `${m.lat},${m.lng}`)
            .join('|');
        chunks.push(`markers=size:tiny|color:0x${color}|${group}`);
    }
    const markersStr = chunks.length ? `&${chunks.join('&')}` : '';

    if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const visible = `visible=${ne.lat()},${ne.lng()}|${sw.lat()},${sw.lng()}`;
        return `${base}?${visible}&${sizeStr}${markersStr}&key=${apiKey}`;
    }
    if (centerZoom) {
        const { lat, lng, zoom } = centerZoom;
        return `${base}?center=${lat},${lng}&zoom=${zoom}&${sizeStr}${markersStr}&key=${apiKey}`;
    }
    return `${base}?center=${DEFAULT_CENTER.lat},${DEFAULT_CENTER.lng}&zoom=${DEFAULT_ZOOM}&${sizeStr}${markersStr}&key=${apiKey}`;
}

const MapPane: React.FC<
    MapPaneProps & { brand: string; isLoaded: boolean; jsApiKey: string; staticKey?: string }
> = ({ title, data, mode, brand, isLoaded, jsApiKey, staticKey, id }) => {
    const mapRef = useRef<google.maps.Map | null>(null);
    const bounds = useBoundsFromData(data, isLoaded);

    const [isPrinting, setIsPrinting] = useState(false);

    useEffect(() => {
        const before = () => setIsPrinting(true);
        const after = () => setIsPrinting(false);
        window.addEventListener('beforeprint', before);
        window.addEventListener('afterprint', after);
        return () => {
            window.removeEventListener('beforeprint', before);
            window.removeEventListener('afterprint', after);
        };
    }, []);

    const heatData = useMemo<HeatmapLayerProps['data']>(() => {
        if (!isLoaded || mode !== 'heat') return [];
        return generateHeatMapData(data, MapDataType.HeatActual);
    }, [data, mode, isLoaded]);

    const markerData = useMemo(() => {
        if (!isLoaded || mode !== 'markers') return [];
        return generateMarkerLocations(data, MapDataType.HeatActual);
    }, [data, mode, isLoaded]);

    const markersForPrint = useMemo(
        () =>
            mode === 'markers'
                ? markerData
                      .filter(m => m.location)
                      .map(m => ({ lat: m.location.lat(), lng: m.location.lng() }))
                : [],
        [markerData, mode],
    );

    const effectiveStaticKey =
        staticKey ||
        (typeof import.meta !== 'undefined' &&
            (import.meta as any).env?.VITE_GOOGLE_STATIC_MAPS_KEY) ||
        // eslint-disable-next-line no-underscore-dangle
        (typeof window !== 'undefined' && (window as any).__GOOGLE_STATIC_MAPS_KEY__) ||
        jsApiKey;

    const [snapshotUrl, setSnapshotUrl] = useState('');
    const [snapshotReady, setSnapshotReady] = useState(false);

    const center = useMemo(() => (bounds ? undefined : DEFAULT_CENTER), [bounds]);
    const zoom = useMemo(() => (bounds ? undefined : DEFAULT_ZOOM), [bounds]);

    const refreshSnapshot = useCallback(() => {
        if (!effectiveStaticKey) return;

        let url: string;

        if (isPrinting && bounds) {
            const padded = padBounds(bounds, 0.14);
            url = buildStaticMapUrl({
                apiKey: effectiveStaticKey,
                bounds: padded,
                markers: markersForPrint,
                brand,
                size: { w: 640, h: 640 },
                scale: 2,
            });
        } else if (mapRef.current) {
            const c = mapRef.current.getCenter?.() || null;
            const z = mapRef.current.getZoom?.() ?? DEFAULT_ZOOM;

            if (c) {
                url = buildStaticMapUrl({
                    apiKey: effectiveStaticKey,
                    centerZoom: {
                        lat: c.lat(),
                        lng: c.lng(),
                        zoom: Math.min(z, MAX_ZOOM_AFTER_FIT),
                    },
                    markers: markersForPrint,
                    brand,
                    size: { w: 640, h: 640 },
                    scale: 2,
                });
            } else if (bounds) {
                url = buildStaticMapUrl({
                    apiKey: effectiveStaticKey,
                    bounds,
                    markers: markersForPrint,
                    brand,
                    size: { w: 640, h: 640 },
                    scale: 2,
                });
            } else {
                url = buildStaticMapUrl({
                    apiKey: effectiveStaticKey,
                    centerZoom: {
                        lat: DEFAULT_CENTER.lat,
                        lng: DEFAULT_CENTER.lng,
                        zoom: DEFAULT_ZOOM,
                    },
                    markers: markersForPrint,
                    brand,
                    size: { w: 640, h: 640 },
                    scale: 2,
                });
            }
        }

        setSnapshotReady(false);
        setSnapshotUrl(url);
    }, [effectiveStaticKey, markersForPrint, brand, bounds, isPrinting]);

    useEffect(() => {
        if (!effectiveStaticKey) return;
        if (!bounds && !mapRef.current) return;
        refreshSnapshot();
    }, [refreshSnapshot, effectiveStaticKey, bounds]);

    useEffect(() => {
        if (!snapshotUrl) return;
        const img = new Image();
        img.loading = 'eager' as any;
        img.referrerPolicy = 'no-referrer-when-downgrade';
        img.onload = () => setSnapshotReady(true);
        img.onerror = () => setSnapshotReady(false);
        img.src = snapshotUrl;
    }, [snapshotUrl]);

    useEffect(() => {
        const handler = () => {
            const g = (window as any)?.google;
            if (mapRef.current && g?.maps?.event) {
                g.maps.event.trigger(mapRef.current, 'resize');
                if (bounds) mapRef.current.fitBounds(bounds);
            }
            refreshSnapshot();
        };

        window.addEventListener('beforeprint', handler);
        return () => window.removeEventListener('beforeprint', handler);
    }, [bounds, refreshSnapshot]);

    const onLoad = useCallback(
        (map: google.maps.Map) => {
            mapRef.current = map;

            if (bounds) {
                map.fitBounds(bounds);

                const g = (window as any)?.google;
                if (g?.maps?.event) {
                    g.maps.event.addListenerOnce(map, 'idle', () => {
                        const z = map.getZoom() ?? DEFAULT_ZOOM;

                        map.setZoom(Math.max(0, z - ZOOM_OUT_AFTER_FIT));

                        refreshSnapshot();
                    });
                }
            } else {
                map.setCenter(DEFAULT_CENTER);
                map.setZoom(DEFAULT_ZOOM);
                refreshSnapshot();
            }
        },
        [bounds, refreshSnapshot],
    );

    useEffect(() => {
        if (!mapRef.current || !bounds) return;

        const map = mapRef.current;
        const g = (window as any)?.google;

        const t = setTimeout(() => {
            map.fitBounds(bounds);

            if (g?.maps?.event) {
                g.maps.event.addListenerOnce(map, 'idle', () => {
                    const z = map.getZoom() ?? DEFAULT_ZOOM;
                    map.setZoom(Math.max(0, z - ZOOM_OUT_AFTER_FIT));
                    refreshSnapshot();
                });
            } else {
                refreshSnapshot();
            }
        }, 120);

        // eslint-disable-next-line consistent-return
        return () => clearTimeout(t);
    }, [bounds, refreshSnapshot]);

    const onUnmount = useCallback(() => {
        mapRef.current = null;
    }, []);

    const mapId = id || `map-${title.toLowerCase().replace(/\W+/g, '-')}`;

    return (
        <ChartCard title={title} brand={brand}>
            <style>{PRINT_SAFE_CSS}</style>

            <Box
                className={`mappane ${snapshotReady ? 'snapshot-ready' : ''}`}
                sx={{ width: '100%', height: SCREEN_MAP_HEIGHT }}
            >
                <Box id={mapId} className="layer live">
                    {isLoaded ? (
                        <GoogleMap
                            onLoad={onLoad}
                            onUnmount={onUnmount}
                            mapContainerStyle={mapContainerStyle}
                            options={ReportingMapOptions}
                            center={center}
                            zoom={zoom}
                        >
                            {mode === 'heat' && <DeckHeatmapLayer data={heatData} />}

                            {mode === 'markers' && (
                                <MarkerClusterer
                                    maxZoom={14}
                                    options={{ imagePath: '/mapclustericons/m' }}
                                    ignoreHidden
                                >
                                    {clusterer => (
                                        <>
                                            {markerData.slice(0, 5000).map((m, i) =>
                                                m.location ? (
                                                    <Marker
                                                        key={m.frameId ?? i}
                                                        position={m.location}
                                                        clusterer={clusterer}
                                                        visible={!m.hidden}
                                                        icon={{
                                                            path: 'M3 5h18v12H3z, M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z',
                                                            fillColor: brand,
                                                            strokeColor: '#ffffff',
                                                            fillOpacity: 1,
                                                            scale: 1,
                                                            anchor:
                                                                typeof window !== 'undefined' &&
                                                                (window as any).google
                                                                    ? new (
                                                                          window as any
                                                                      ).google.maps.Point(12.5, 25)
                                                                    : undefined,
                                                        }}
                                                    />
                                                ) : null,
                                            )}
                                        </>
                                    )}
                                </MarkerClusterer>
                            )}
                        </GoogleMap>
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'grid',
                                placeItems: 'center',
                                color: 'text.secondary',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            Loading map…
                        </Box>
                    )}
                </Box>

                {effectiveStaticKey && snapshotUrl && (
                    <Box className="layer snapshot" aria-hidden>
                        <img src={snapshotUrl} alt="" />
                    </Box>
                )}
            </Box>
        </ChartCard>
    );
};

const OneMapPage: React.FC<OneMapPageProps> = ({ mainTitle, brand, logoUrl, footer, map }) => {
    const jsApiKey =
        (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GOOGLE_MAPS_KEY) ||
        // eslint-disable-next-line no-underscore-dangle
        (typeof window !== 'undefined' && (window as any).__GOOGLE_MAPS_API_KEY__) ||
        '';

    const staticKey =
        (typeof import.meta !== 'undefined' &&
            (import.meta as any).env?.VITE_GOOGLE_STATIC_MAPS_KEY) ||
        // eslint-disable-next-line no-underscore-dangle
        (typeof window !== 'undefined' && (window as any).__GOOGLE_STATIC_MAPS_KEY__) ||
        undefined;

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: jsApiKey,
        libraries: ['visualization'],
    });

    if (loadError) return <Box sx={{ p: 2 }}>Map failed to load.</Box>;
    if (!isLoaded) return <Box sx={{ p: 2 }}>Loading map…</Box>;

    return (
        <Box
            className="page page--maps"
            sx={{ mx: 'auto', fontFamily: 'Inter, system-ui, sans-serif' }}
        >
            <style>{PRINT_SAFE_CSS}</style>

            <Box className="page-header">
                <ReportHeader brand={brand} logoUrl={logoUrl} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 900, mb: 1.5 }}>
                {mainTitle}
            </Typography>

            <MapPane
                id={map.id || 'map-single'}
                title={map.title}
                data={map.data}
                mode={map.mode}
                brand={brand}
                isLoaded={isLoaded && !loadError}
                jsApiKey={jsApiKey}
                staticKey={staticKey}
            />

            <Box className="page-footer">
                <ReportFooter {...footer} />
            </Box>
        </Box>
    );
};

export default OneMapPage;
